import type { ReactNode } from 'react'
import { normalizeCurrencyTextCompact } from './cfoFormat'

// CfoStatementAiHeadline — the AI's level-1 headline for a financial statement, mounted above the
// statement grid. Driven by the month-end-close engine's per-statement headline + drivers (served by
// getQuickBooksFinancialsAiAnnotations). Verdict-first and plain-English; drivers deep-link to rows.

type Driver = { node_id?: string | null; direction?: string; text?: string }

export type ReadinessCheck = {
  id: string
  status?: string // 'pass' | 'warn' | 'fail'
  amount?: number | null
  note?: string | null
  acknowledged?: boolean
}

export type StatementAnnotations = {
  has_report?: boolean
  layout_stale?: boolean
  period?: string | null
  status?: string
  /** When the close review content was last (re)generated — the review is a snapshot of the books
   * as of this moment, which can lag the live statement when entries post after month end. */
  generated_at?: string | null
  /** Server-computed: the month's books changed since this review was authored. `false` = still
   * current; `true` = stale (the page kicks an on-view refresh); absent = undeterminable cheaply
   * (legacy row) — the refresh endpoint decides. */
  data_stale?: boolean | null
  headline?: { tone?: string; text?: string } | null
  drivers?: Driver[]
  readiness?: { score?: number; overall?: string; checks?: ReadinessCheck[] } | null
  lines?: unknown[]
}

const toneStyles = (tone: string | undefined): { color: string; bg: string; border: string } => {
  switch (tone) {
    case 'good':
      return { color: 'var(--pos)', bg: 'rgba(14,124,134,0.10)', border: 'rgba(14,124,134,0.30)' }
    case 'concern':
      return { color: 'var(--neg)', bg: 'rgba(192,73,47,0.10)', border: 'rgba(192,73,47,0.30)' }
    default: // caution
      return { color: '#b8860b', bg: 'rgba(184,134,11,0.10)', border: 'rgba(184,134,11,0.30)' }
  }
}

const dirGlyph = (dir: string | undefined): string =>
  dir === 'up' ? '▲' : dir === 'down' ? '▼' : '•'

const dirColor = (dir: string | undefined): string =>
  dir === 'up' ? 'var(--pos)' : dir === 'down' ? 'var(--neg)' : 'var(--muted, #888)'

/** "2026-06-01" → "June 2026" (UTC-pinned so the month never shifts across timezones). */
const monthLabel = (period: string | null | undefined): string | null => {
  const m = typeof period === 'string' ? period.match(/^(\d{4})-(\d{2})/) : null
  if (!m) return null
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

const reviewedLabel = (iso: string | null | undefined): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function CfoStatementAiHeadline({
  annotations,
  loading,
  running,
  updating,
  onRun,
  onDriverClick,
  canFocus,
}: {
  annotations: StatementAnnotations | null
  loading?: boolean
  running?: boolean
  /** An on-view history refresh is rewriting this month's review (books changed since it ran) —
   * show the notice and dim the soon-to-be-replaced content. */
  updating?: boolean
  onRun?: () => void
  onDriverClick?: (nodeId: string) => void
  /** Whether a driver's node_id resolves to a real statement row (so the bullet should deep-link). */
  canFocus?: (nodeId: string) => boolean
}): ReactNode {
  const headline = annotations?.headline
  // The headline narrative is no longer rendered (the drivers ARE the review now), so a report counts
  // as present when we have either a headline or at least one driver.
  const driverList = Array.isArray(annotations?.drivers) ? annotations!.drivers! : []
  const hasHeadline =
    annotations?.has_report !== false && (!!headline?.text || driverList.length > 0)

  // Report materialization owns automatic refresh. Show progress only while the
  // review is loading or an existing refresh is running.
  if (!hasHeadline && (loading || running)) {
    return (
      <section
        className="rise"
        style={{ margin: '0 0 14px', padding: '14px 16px', borderRadius: 12, background: 'var(--surface-2,#f4f4f5)' }}
      >
        <span style={{ fontSize: 14, color: 'var(--muted, #888)' }}>
          {running ? 'Generating your AI review…' : 'Loading the AI read…'}
        </span>
      </section>
    )
  }

  // No review yet and nothing in flight: allow an explicit review request.
  if (!hasHeadline) {
    return (
      <section
        className="rise"
        style={{
          margin: '0 0 14px',
          padding: '12px 16px',
          borderRadius: 12,
          background: 'var(--surface-2,#f4f4f5)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 14, color: 'var(--muted, #666)' }}>
          No AI review yet for this statement.
        </span>
        {onRun ? (
          <button className="btn" disabled={running} onClick={onRun}>
            {running ? 'Starting…' : 'Run AI review'}
          </button>
        ) : null}
      </section>
    )
  }

  const tone = toneStyles(headline?.tone)
  const toneLabel =
    headline?.tone === 'good'
      ? 'What stood out'
      : headline?.tone === 'concern'
        ? 'What needs attention'
        : 'What to look at'
  // The review's vintage, stated up front: it's a snapshot of the books at review time, and entries
  // often post for weeks after month end — without this label a stale read is indistinguishable
  // from a fresh one (it just looks wrong against the live statement).
  const reviewMonth = monthLabel(annotations?.period)
  const reviewedAt = reviewedLabel(annotations?.generated_at)
  const vintage = reviewMonth
    ? `${reviewMonth} close${reviewedAt ? ` · reviewed ${reviewedAt}` : ''}`
    : null

  return (
    <section
      className="rise"
      style={{
        margin: '0 0 14px',
        padding: '14px 18px',
        borderRadius: 14,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: driverList.length ? 10 : 0,
        }}
      >
        <span aria-hidden style={{ fontSize: 13, color: tone.color }}>✦</span>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: tone.color,
          }}
        >
          {toneLabel}
        </span>
        {vintage ? (
          <>
            <span style={{ flex: 1 }} />
            <span
              title="The AI review is a snapshot of the books when it ran. If entries have posted since, re-run the AI refresh to update it."
              style={{ fontSize: 11.5, color: 'var(--muted, #888)', whiteSpace: 'nowrap' }}
            >
              {vintage}
            </span>
          </>
        ) : null}
      </div>

      {updating ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            padding: '8px 10px',
            marginBottom: 8,
            borderRadius: 9,
            background: 'rgba(255,255,255,0.55)',
            border: `1px dashed ${tone.border}`,
            fontSize: 13.5,
            lineHeight: 1.5,
          }}
        >
          <span aria-hidden style={{ color: tone.color }}>↻</span>
          <span>
            The books for {reviewMonth || 'this month'} changed after this review — the AI is
            re-reading the month now. This usually takes under a minute; the review below will
            update automatically.
          </span>
        </div>
      ) : null}

      {driverList.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: updating ? 0.5 : 1 }}>
          {driverList.map((d, i) => {
            const clickable =
              !!d.node_id && !!onDriverClick && (canFocus ? canFocus(d.node_id) : true)
            return (
              <div
                key={i}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onDriverClick!(d.node_id as string) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onDriverClick!(d.node_id as string)
                        }
                      }
                    : undefined
                }
                title={clickable ? 'Jump to this line in the statement' : undefined}
                className={clickable ? 'cfo-ai-driver cfo-ai-driver-clickable' : 'cfo-ai-driver'}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'baseline',
                  padding: '7px 8px',
                  borderRadius: 9,
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    color: dirColor(d.direction),
                    fontSize: 11,
                    lineHeight: 1.5,
                    flex: 'none',
                    width: 12,
                    textAlign: 'center',
                  }}
                >
                  {dirGlyph(d.direction)}
                </span>
                <span style={{ fontSize: 14.5, lineHeight: 1.55, flex: 1 }}>{normalizeCurrencyTextCompact(d.text)}</span>
                {clickable ? (
                  <span
                    aria-hidden
                    className="cfo-ai-driver-chevron"
                    style={{ color: tone.color, fontSize: 13, flex: 'none', opacity: 0.55 }}
                  >
                    →
                  </span>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : (
        // Defensive: a report with no drivers still shows its narrative so the band is never empty.
        <div style={{ fontSize: 14.5, lineHeight: 1.55, opacity: updating ? 0.5 : 1 }}>
          {normalizeCurrencyTextCompact(headline?.text)}
        </div>
      )}

      {annotations?.layout_stale ? (
        <div style={{ fontSize: 12, color: 'var(--muted, #888)', marginTop: 10 }}>
          Based on the close from {annotations.period}. The statement layout changed since then —
          re-run the AI review to refresh per-line flags.
        </div>
      ) : null}
    </section>
  )
}

export default CfoStatementAiHeadline
